import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import { TCategoryItem } from './SideMenuItem';
import { EFromLinkPosition } from '@citygross/analytics';
import { Icons } from '@citygross/icons';
import { theme } from '@citygross/design-tokens';
import { ga4FavoriteOrPurchaseLinkClick } from '../../../../lib/analytics/analytics';
import { categoryItemType, getLinkAttributes } from '../../../../lib/sideMenu';
import { closeSideModal } from '../../../../slices/sideModalSlice';
import * as styles from './SideMenuItem.styles';

type TCategoryItemProps = {
  categoryItem: TCategoryItem;
  isSidebar?: boolean;
};

export const CategoryItem = ({
  categoryItem,
  isSidebar
}: TCategoryItemProps) => {
  if (categoryItem?.type === categoryItemType.ContentStructureContainer) {
    return (
      <styles.SideMenuSpacerItem isSidebar={isSidebar}>
        {categoryItem?.header}
      </styles.SideMenuSpacerItem>
    );
  }

  if (!categoryItem?.visible || !categoryItem?.link?.url) return null;

  const linkUrl = categoryItem.link?.url;
  const categoryLevel = useMemo(() => linkUrl.split('/').length - 2, [linkUrl]);

  const linkAttributes = getLinkAttributes({ categoryLevel });

  const hasChildren = categoryItem?.children?.length > 0;
  const isLastItem = !hasChildren;

  const location = useLocation();
  const [isOpen, setIsOpen] = useState(
    categoryLevel && hasChildren && location.pathname.startsWith(linkUrl)
  );

  const dispatch = useDispatch();

  const handleNavLinkClick = () => {
    if (hasChildren) setIsOpen(true);

    if (isLastItem && linkUrl !== location.pathname) {
      ga4FavoriteOrPurchaseLinkClick(linkUrl, EFromLinkPosition.SIDE_MODAL);
    }

    dispatch(closeSideModal());
  };

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (location.pathname === linkUrl && ref.current) {
      ref.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [linkUrl, location.pathname]);

  return (
    <Fragment>
      <styles.LinkContainer
        backgroundColor={linkAttributes.backgroundColor}
        hoverBackground={linkAttributes.hoverBackground}
        isOpen={isOpen}
        ref={ref}
      >
        <NavLink
          aria-label={categoryItem?.name}
          exact
          onClick={handleNavLinkClick}
          style={{
            flex: 1,
            minWidth: 0,
            padding: theme.spacings?.xs,
            paddingLeft: categoryLevel
              ? categoryLevel * (theme.spacings?.sm ?? 16)
              : theme.spacings?.sm
          }}
          to={{
            pathname: linkUrl,
            state: { from: 'category-nav' }
          }}
        >
          <styles.MenuItemLabel
            fontColor={
              isLastItem && location?.pathname === linkUrl
                ? theme.palette?.brandBlue
                : theme.palette?.darkest
            }
          >
            {categoryItem.name}
          </styles.MenuItemLabel>
        </NavLink>

        {hasChildren && (
          <styles.IconButton
            aria-controls={`submenu-${categoryItem.id}`}
            aria-expanded={isOpen}
            aria-label={`${isOpen ? 'Stäng' : 'Öppna'} ${categoryItem?.name}`}
            onClick={() => setIsOpen(prev => !prev)}
          >
            {isOpen ? (
              <Icons.Minus
                height={theme.constants?.menuListItemIconSize}
                width={theme.constants?.menuListItemIconSize}
              />
            ) : (
              <Icons.Plus
                height={theme.constants?.menuListItemIconSize}
                width={theme.constants?.menuListItemIconSize}
              />
            )}
          </styles.IconButton>
        )}
      </styles.LinkContainer>

      {hasChildren && isOpen && (
        <li aria-expanded id={`submenu-${categoryItem.id}`}>
          {categoryItem.children.map(item => (
            <CategoryItem categoryItem={item} key={item.id} />
          ))}
        </li>
      )}
    </Fragment>
  );
};
